<template>
  <v-dialog v-model="visible" max-width="560" scrollable @keydown.esc="close">
    <v-card>
      <v-card-title class="text-h6 font-weight-bold primary white--text">
        Agregar documentos al reembolso
      </v-card-title>

      <v-card-text class="pt-5 pb-2">
        <div v-if="loading" class="d-flex justify-center py-8">
          <v-progress-circular indeterminate color="primary"></v-progress-circular>
        </div>

        <div v-else>
          <!-- Documentos requeridos faltantes -->
          <template v-if="missingRequired.length > 0">
            <span class="subtitle-2 font-weight-bold primary--text">Documentos requeridos faltantes</span>
            <v-divider style="margin-top: 12px; margin-bottom: 20px"></v-divider>
            <div v-for="type in missingRequired" :key="type.id" class="mb-4">
              <p class="caption font-weight-bold primary--text mb-1">{{ type.name }}</p>
              <v-file-input
                v-model="requiredFiles[type.id]"
                outlined
                dense
                show-size
                hide-details
                placeholder="Seleccionar archivo"
                prepend-icon=""
                prepend-inner-icon="mdi-paperclip"
                accept="image/jpeg,image/png,application/pdf"
              ></v-file-input>
            </div>
          </template>

          <!-- Documentos adicionales (opcionales, cualquier tipo, incluso ya enviados) -->
          <template v-if="extraTypes.length > 0">
            <div :class="missingRequired.length > 0 ? 'mt-8' : ''">
              <span class="subtitle-2 font-weight-bold primary--text">Documentos adicionales</span>
              <span class="caption grey--text ml-2">(opcional)</span>
            </div>
            <v-divider style="margin-top: 12px; margin-bottom: 20px"></v-divider>
            <div v-for="(extra, index) in extraDocs" :key="extra.key" class="d-flex align-start mb-4">
              <div class="flex-grow-1">
                <v-select
                  v-model="extra.typeId"
                  :items="extraTypes"
                  item-text="name"
                  item-value="id"
                  outlined
                  dense
                  hide-details
                  label="Tipo de documento"
                  class="mb-2"
                ></v-select>
                <v-file-input
                  v-model="extra.files"
                  multiple
                  chips
                  small-chips
                  outlined
                  dense
                  show-size
                  hide-details
                  placeholder="Seleccionar archivos"
                  prepend-icon=""
                  prepend-inner-icon="mdi-paperclip"
                  accept="image/jpeg,image/png,application/pdf"
                ></v-file-input>
              </div>
              <v-btn v-if="extraDocs.length > 1" icon small class="ml-2 mt-1" @click="removeExtraDoc(index)">
                <v-icon small>mdi-close</v-icon>
              </v-btn>
            </div>
            <v-btn text small color="primary" class="px-0" @click="addExtraDoc">
              <v-icon left small>mdi-plus</v-icon>
              Agregar otro tipo de documento
            </v-btn>
          </template>

          <p v-if="missingRequired.length === 0 && extraTypes.length === 0" class="body-2 grey--text py-4">
            El reembolso ya tiene todos los documentos requeridos y no hay documentos opcionales disponibles.
          </p>

          <!-- Comentarios (siempre visible) -->
          <div :class="missingRequired.length > 0 || extraTypes.length > 0 ? 'mt-8' : ''">
            <span class="subtitle-2 font-weight-bold primary--text">Comentarios</span>
          </div>
          <v-divider style="margin-top: 12px; margin-bottom: 20px"></v-divider>
          <v-textarea
            v-model="comments"
            outlined
            dense
            rows="3"
            hide-details
            placeholder="Utiliza este espacio para añadir información que facilite la revisión de la solicitud"
          ></v-textarea>
        </div>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions class="px-4 py-3">
        <v-spacer></v-spacer>
        <v-btn text @click="close">Cancelar</v-btn>
        <v-btn
          color="primary"
          depressed
          :loading="saving"
          :disabled="loading || !hasContent"
          @click="save"
        >
          Guardar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import RefundsServices from '../services/refunds.service';
import ServicesService from '../services/services.service';
import FileUtils from '../utils/file.utils';

export default {
  name: 'AddRefundDocumentsModal',
  props: {
    refundId: {
      type: String,
      required: true
    },
    serviceId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      visible: false,
      loading: false,
      saving: false,
      missingRequired: [],
      extraTypes: [],
      defaultExtraTypeId: null,
      extraDocs: [],
      extraDocKey: 0,
      requiredFiles: {},
      comments: ''
    };
  },
  computed: {
    hasContent() {
      const hasRequired = Object.values(this.requiredFiles).some(f => !!f);
      const hasExtra = this.extraDocs.some(e => e.typeId && e.files.length > 0);
      return hasRequired || hasExtra || !!this.comments.trim();
    }
  },
  methods: {
    newExtraDoc() {
      this.extraDocKey += 1;
      return { key: this.extraDocKey, typeId: this.defaultExtraTypeId, files: [] };
    },
    addExtraDoc() {
      this.extraDocs.push(this.newExtraDoc());
    },
    removeExtraDoc(index) {
      this.extraDocs.splice(index, 1);
    },
    async open() {
      this.missingRequired = [];
      this.extraTypes = [];
      this.defaultExtraTypeId = null;
      this.extraDocs = [];
      this.requiredFiles = {};
      this.comments = '';
      this.visible = true;
      this.loading = true;

      try {
        const [inputsRes, refundDetail] = await Promise.all([
          ServicesService.getServiceRequiredInputs(this.serviceId),
          RefundsServices.getRefundDetails(this.refundId).catch(() => null)
        ]);

        if (inputsRes.status !== 'ok' || !inputsRes.data || !inputsRes.data.serviceType) {
          this.$emit('error');
          this.close();
          return;
        }

        const all = inputsRes.data.serviceType.requiredAttachments || [];
        const required = all.filter(
          a => a.name !== 'Documento adicional' && !a.name.toLowerCase().includes('jur')
        );
        // Extras may be of any uploadable type, including ones already sent (e.g. a corrected "Comprobante de pago").
        this.extraTypes = all.filter(a => !a.name.toLowerCase().includes('jur'));
        const optionalType = this.extraTypes.find(a => a.name === 'Documento adicional');
        this.defaultExtraTypeId = optionalType ? optionalType.id : null;
        if (this.extraTypes.length > 0) this.extraDocs = [this.newExtraDoc()];

        const existing = refundDetail ? (refundDetail.files || []) : [];
        const existingTypes = new Set(existing.map(a => a.type).filter(Boolean));

        this.missingRequired = required.filter(a => !existingTypes.has(a.name));

        const initial = {};
        this.missingRequired.forEach(a => { initial[a.id] = null; });
        this.requiredFiles = initial;
      } finally {
        this.loading = false;
      }
    },
    close() {
      this.visible = false;
    },
    async save() {
      this.saving = true;
      try {
        const requiredAttachments = await Promise.all(
          this.missingRequired
            .filter(t => !!this.requiredFiles[t.id])
            .map(async (t) => {
              const file = this.requiredFiles[t.id];
              return {
                attachmentTypeId: t.id,
                base64Attachment: await FileUtils.getBase64(file),
                fileName: file.name
              };
            })
        );

        const extraAttachments = await Promise.all(
          this.extraDocs
            .filter(e => !!e.typeId)
            .flatMap(e => e.files.map(file => ({ typeId: e.typeId, file })))
            .map(async ({ typeId, file }) => ({
              attachmentTypeId: typeId,
              base64Attachment: await FileUtils.getBase64(file),
              fileName: file.name
            }))
        );

        const attachments = [...requiredAttachments, ...extraAttachments];

        const res = await RefundsServices.addAttachments(this.refundId, attachments, this.comments || undefined);
        if (res.status === 'ok') {
          this.$emit('saved');
          this.close();
        } else {
          this.$emit('error');
        }
      } catch {
        this.$emit('error');
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>
